import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CourseTypeTable from '../../../components/tables/CourseTypeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCourseTypeSearch, useDeleteCourseType } from '../../../hooks/useCourseTypeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CourseTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const CourseTypeList: React.FC = () => {
  const { search } = useCourseTypeSearch();
  const deleteCourseType = useDeleteCourseType();
  const [items, setItems] = useState<CourseTypeDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Course Types', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Course Types:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: CourseTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteCourseType.mutateAsync(dto.id!);
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error deleting CourseType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/coursetypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.CourseType.View"
    >
      <Card
        title="Manage Course Types"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/coursetypes/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
            <FormField
              id="titleShort"
              label="Search by Title Short"
              value={searchFilters.titleShort || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  titleShort: e.target.value,
                })
              }
              placeholder="Search by Title Short"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CourseTypeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/coursetypes/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CourseTypeList;
